'use client';

import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useHandleError } from '@/hooks/useHandleError';
import { apiFetch } from '@/lib/api/apiClient';
import { useAuthStore } from '@/stores/useAuthStore';
import { Badge } from '@/types/achievements';
import ImageAdaptive from '@/components/ImageAdaptive';
import BadgeUnit from '@/components/features/dashboard/modal/DashboardAchievementBadgeUnit';

export default function AwardBadgeNotification() {
    const [pendingAwardBadges, setPendingAwardBadges] = useState<Badge[]>([]);
    const [currentBadgeIndex, setCurrentBadgeIndex] = useState(0);
    const { handleError } = useHandleError();
    const setUserScore = useAuthStore((s) => s.setUserScore);

    useEffect(() => {
        (async () => {
            try {
                const data = await apiFetch(`/users/me/achievement/award/badge`, {
                    method: 'POST',
                });
                setPendingAwardBadges(data?.award_badges);
            } catch (error: unknown) {
                handleError(error);
            }
        })();
    }, []);

    useEffect(() => {
        (async () => {
            try {
                const scoreData = await apiFetch(`/users/my_score`);
                setUserScore({
                    achievementScore: scoreData.achievement_score,
                    totalMedalScore: scoreData.total_medal_score,
                    totalBadgeScore: scoreData.total_badge_score,
                    gearPostCount: scoreData.gear_post_count,
                    gearLikeCount: scoreData.gear_like_count,
                    festaEntryCheckinCount: scoreData.festa_entry_checkin_count,
                    totalScore: scoreData.total_score,
                    ownedLabCount: scoreData.owned_lab_count,
                    editorLaunchCount: scoreData.editor_launch_count,
                    whiteTeacherAnswerCount: scoreData.white_teacher_answer_count,
                    editorWriteCount: scoreData.editor_write_count,
                });
            } catch (error: unknown) {
                handleError(error);
            }
        })();
    }, [pendingAwardBadges]);

    const handleNextBadge = () => {
        if (currentBadgeIndex < pendingAwardBadges.length - 1) {
            setCurrentBadgeIndex(currentBadgeIndex + 1);
        }
    };

    const handlePreviousBadge = () => {
        if (currentBadgeIndex > 0) {
            setCurrentBadgeIndex(currentBadgeIndex - 1);
        }
    };

    const currentBadge = pendingAwardBadges[currentBadgeIndex];

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2, delay: 0.5 }}
            className="flex flex-col justify-center items-center px-1 lg:px-5 w-full h-full"
        >
            <div className="relative flex justify-center items-center w-full h-full">
                <div className="flex w-[50px] justify-end px-2">
                    {currentBadgeIndex > 0 ? (
                        <button onClick={handlePreviousBadge}>
                            <ImageAdaptive src={`/images/achievement/back.svg`} baseWidth={30} />
                        </button>
                    ) : (
                        <ImageAdaptive src={`/images/achievement/back-disabled.svg`} baseWidth={30} />
                    )}
                </div>
                <div className="flex flex-col w-full justify-center items-center">
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.0, delay: 0.0 }}
                        className="font-corp font-bold text-xl lg:text-3xl flex items-center justify-center text-center pb-4"
                    >
                        バッジ獲得！
                    </motion.div>
                    {currentBadge && (
                        <>
                            <div className="font-notojp font-medium text-xs flex flex-col items-center justify-center text-center">
                                <div className="flex justify-center items-center w-full lg:w-[250px]">
                                    <ImageAdaptive
                                        src={`/images/achievement/badge/frame/badge-award-frame.svg`}
                                        baseWidth={250}
                                        className="relative"
                                    />
                                </div>
                                <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-3/4 mt-[-15px] lg:mt-[-18px] w-[100px] min-w-[100px] lg:w-[100px] lg:min-w-[100px]">
                                    <BadgeUnit
                                        badgeCd={currentBadge.badge_cd}
                                        achievementScore={currentBadge.achievement_score}
                                        isAwarded={currentBadge.award_status === 'pending'}
                                    />
                                </div>
                                <div className="flex flex-col flex-grow justify-center items-center w-full">
                                    <div className="grid grid-cols-2 grid-rows-1 gap-4 w-full h-full">
                                        <div className="col-span-2 row-span-2 w-full h-full flex justify-center items-center mt-2 mb-8">
                                            <div className="flex flex-col">
                                                <div className="font-corp font-bold text-base lg:text-xl text-color-base">
                                                    {currentBadge.badge_name}
                                                </div>
                                                <div className="font-notojp font-medium text-xs text-color-base pt-1">
                                                    {currentBadge.badge_description}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="w-full flex flex-row">
                                        <div className="w-[120px] h-full flex justify-center items-center">
                                            <div className="flex flex-col lg:flex-row justify-center lg:items-center font-corp font-bold text-sm text-color-base w-full lg:h-[50px]">
                                                <div className="text-xs lg:text-sm">獲得スコア</div>
                                                <div className="font-montserrat font-medium text-base lg:text-xl text-color-c-orange pl-0 lg:pl-4">
                                                    {currentBadge.achievement_score}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="h-full flex flex-grow justify-center items-center">
                                            <div className="flex flex-col lg:flex-row justify-center lg:items-center font-corp font-bold text-color-base w-full lg:h-[50px]">
                                                <div className="text-xs lg:text-sm">獲得日</div>
                                                <div className="font-montserrat font-medium text-xs tracking-widest text-color-base pl-0 lg:pl-4 lg:pt-0 pt-1">
                                                    {currentBadge.award_date_time}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </>
                    )}
                </div>
                <div className="flex w-[50px] justify-start px-2">
                    {currentBadgeIndex < pendingAwardBadges.length - 1 ? (
                        <button onClick={handleNextBadge}>
                            <ImageAdaptive src={`/images/achievement/next.svg`} baseWidth={30} />
                        </button>
                    ) : (
                        <ImageAdaptive src={`/images/achievement/next-disabled.svg`} baseWidth={30} />
                    )}
                </div>
            </div>
        </motion.div>
    );
}
